import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error during auth callback:', error);
          navigate(`/${language}/sign-in`);
          return;
        }

        // Check if this is an email verification
        const hash = window.location.hash;
        if (hash && hash.includes('type=signup')) {
          setVerificationSuccess(true);
          setTimeout(() => {
            navigate(`/${language}/sign-in`);
          }, 3000);
          return;
        }

        if (session?.user) {
          console.log('Auth callback - user found:', session.user.id);
          
          // Check if profile exists
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          console.log('Profile check result:', { profile, profileError });

          // If no profile exists or there was a not-found error, create one
          if (!profile || profileError?.code === 'PGRST116') {
            console.log('Creating new profile for user:', session.user.id);
            
            try {
              // First attempt to create profile
              const { error: insertError } = await supabase
                .from('profiles')
                .upsert([
                  {
                    id: session.user.id,
                    email: session.user.email,
                    credits: 3, // Give new users 3 initial credits
                    is_subscribed: false,
                    conversions_count: 0,
                    downloads_count: 0
                  }
                ], 
                { 
                  onConflict: 'id'
                });

              if (insertError) {
                console.error('Error creating profile:', insertError);
                throw insertError;
              }

              // Verify the profile was created
              const { data: verifyProfile, error: verifyError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();

              console.log('Profile verification:', {
                profile: verifyProfile,
                error: verifyError,
                credits: verifyProfile?.credits
              });

              if (verifyError) {
                console.error('Error verifying profile:', verifyError);
              }

              console.log('Successfully created/verified profile');
            } catch (error) {
              console.error('Profile creation/verification error:', error);
              // Don't throw the error here, just log it and continue
              // This prevents the sign-in process from hanging
            }
          }

          navigate(`/${language}/profile`);
        } else {
          console.log('No session found in auth callback');
          navigate(`/${language}/sign-in`);
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        navigate(`/${language}/sign-in`);
      }
    };

    handleAuthCallback();
  }, [navigate, language]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      {verificationSuccess ? (
        <div className="text-center p-8 bg-green-500/10 border border-green-500/20 rounded-lg">
          <h2 className="text-2xl font-bold text-green-400 mb-4">
            {t('auth.verificationSuccess')}
          </h2>
          <p className="text-green-400/80">
            {t('auth.redirectingToSignIn')}
          </p>
        </div>
      ) : (
        <div className="text-white">
          {t('auth.processing')}
        </div>
      )}
    </div>
  );
};

export default AuthCallback;